import { ALL_DAYS } from "@/lib/days";
import type {
  RecurringTasksConfig,
  RecurringTasksDayData,
  RecurringTasksWeekData,
  WidgetConfig,
  WidgetWeekEntry,
} from "@/lib/schema";
import { countPhrase, sumOverDays } from "@/widgets/describe";
import { getWidgetModule } from "@/widgets/registry";
import type { DayWidgetModule } from "@/widgets/types";
import { DayView } from "./DayView";
import { SettingsEditor } from "./SettingsEditor";
import {
  maxScoreWeek,
  scoreDay,
  scoreWeek,
  tasksForDay,
  weeklyTasksForDay,
} from "./scoring";
import { validate } from "./validate";

function defaults(existing: WidgetConfig[]): RecurringTasksConfig {
  const dayWidgets = existing.filter(
    (w) => getWidgetModule(w.kind)?.scope === "day",
  );
  const maxRow = dayWidgets.reduce((m, w) => Math.max(m, w.row), -1);
  return {
    id: crypto.randomUUID(),
    kind: "recurring-tasks",
    label: "Faste oppgaver",
    row: maxRow + 1,
    column: 0,
    enabled: true,
    points: 1,
    reset: "daily",
    weekGoal: Number.NaN,
    tasks: [
      {
        id: crypto.randomUUID(),
        label: "Drikk vann",
        visibleDays: [...ALL_DAYS],
      },
      {
        id: crypto.randomUUID(),
        label: "Trening",
        visibleDays: ["mon", "wed", "fri"],
      },
    ],
  };
}

// Both storage shapes at once: daily reset ticks live per day, weekly reset
// shares one bit for the week, and this hook is asked about an entry without
// knowing which mode it is in.
function countChecked(checked: Record<string, boolean> | undefined): number {
  if (!checked) return 0;
  return Object.values(checked).filter(Boolean).length;
}

function describeWeekData(entry: WidgetWeekEntry): string | null {
  const week = entry.week as RecurringTasksWeekData | undefined;
  const ticks =
    countChecked(week?.checked) +
    sumOverDays<RecurringTasksDayData>(entry, (day) =>
      countChecked(day?.checked),
    );
  return countPhrase(ticks, "avkryssing", "avkryssinger");
}

export const recurringTasks: DayWidgetModule<RecurringTasksConfig> = {
  kind: "recurring-tasks",
  label: "Faste oppgaver",
  scope: "day",
  scoring: "points",
  defaults,
  validate,
  SettingsEditor,
  DayView,
  describeWeekData,
  isVisibleForDay: (config, dayKey) =>
    config.reset === "weekly"
      ? weeklyTasksForDay(config, dayKey).length > 0
      : tasksForDay(config, dayKey).length > 0,
  scoreDay,
  scoreWeek,
  maxScoreWeek,
};
